import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CheckCircle2, HelpCircle, Loader2, ShieldCheck, History, Lock, ChevronDown, ChevronRight,
} from "lucide-react";
import { useTenant } from "@/context/TenantContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const TRI = { YES: "Sì", NO: "No", UNKNOWN: "Non so" };
const STATUS_LABELS = {
  DRAFT: "Bozza", TO_VERIFY: "Da verificare", VERIFIED: "Verificato",
  APPROVED: "Approvato", NOT_APPLICABLE: "Non applicabile",
};
const STATUS_CLS = {
  DRAFT: "bg-slate-100 text-slate-700", TO_VERIFY: "bg-amber-100 text-amber-800",
  VERIFIED: "bg-green-100 text-green-800", APPROVED: "bg-green-700 text-white",
  NOT_APPLICABLE: "bg-slate-200 text-slate-500",
};
const LEGAL_BY_SECTION = {
  WHISTLEBLOWING: "WHISTLEBLOWING_CHANNEL", CYBER: "NIS2_OBLIGATIONS", MODEL231: "MODEL_231",
};
const LEGAL_LABELS = {
  APPLICABLE: ["Potenzialmente applicabile", "bg-red-100 text-red-800"],
  NOT_APPLICABLE: ["Non applicabile (criteri attuali)", "bg-slate-100 text-slate-600"],
  TO_VERIFY: ["Applicabilità da verificare", "bg-amber-100 text-amber-800"],
};

// Aree di governance canoniche (Fase 7): domande semplici e progressive.
// Il cliente dichiara i fatti; maturity/validazione restano al consulente.
export default function GovernanceCanonical({ onChanged }) {
  const { companyId, period, can } = useTenant();
  const [defs, setDefs] = useState(null);
  const [records, setRecords] = useState({});
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState(null);
  const [textDrafts, setTextDrafts] = useState({});
  const canWrite = can("esg:write") && period?.status !== "LOCKED";
  const canVerify = can("esg:verify");
  const canMaturity = can("analysis:notes:edit");
  const canOverride = can("analysis:plan:edit");
  const base = `/companies/${companyId}/periods/${period?.id}/governance`;

  const load = useCallback(async () => {
    if (!companyId || !period?.id) return;
    try {
      const [{ data: d }, { data: c }] = await Promise.all([
        api.get(`${base}/definitions`), api.get(`${base}/canonical`),
      ]);
      setDefs(d);
      setRecords(Object.fromEntries(c.records.map((r) => [r.governance_code, r])));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [companyId, period?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const save = async (code, value) => {
    setBusy(code);
    try {
      await api.put(`${base}/canonical/${code}`, { value });
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const verify = async (code) => {
    setBusy(code);
    try {
      await api.post(`${base}/canonical/${code}/verify`);
      toast.success("Voce verificata");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const setMaturity = async (code, level) => {
    try {
      await api.put(`${base}/canonical/${code}/maturity`, { level: Number(level) });
      toast.success("Maturity aggiornata (visibile solo ai consulenti)");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const overrideNA = async (code, status) => {
    const motivation = window.prompt("Motivazione dell'override (obbligatoria):");
    if (!motivation || motivation.trim().length < 5) return;
    try {
      await api.post(`${base}/canonical/${code}/applicability`,
        { applicability_status: status, motivation: motivation.trim() });
      toast.success("Applicabilità aggiornata con motivazione registrata");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const confirmCarryForward = async (codes) => {
    try {
      const { data } = await api.post(`${base}/carry-forward/confirm`, { codes });
      toast.success(`${data.confirmed.length} informazioni confermate dall'esercizio precedente`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const isVisible = useCallback((item) => {
    if (!item.depends_on) return true;
    return records[item.depends_on.code]?.value === item.depends_on.equals;
  }, [records]);

  const sectionStats = useMemo(() => {
    if (!defs) return {};
    const stats = {};
    for (const item of defs.items) {
      if (!isVisible(item)) continue;
      const s = stats[item.section] ||= { complete: 0, to_verify: 0, to_complete: 0, not_applicable: 0 };
      const rec = records[item.code];
      if (rec?.applicability_status === "NOT_APPLICABLE" || rec?.status === "NOT_APPLICABLE") s.not_applicable += 1;
      else if (rec && rec.value !== null && rec.value !== undefined && rec.value !== "") {
        if (["VERIFIED", "APPROVED"].includes(rec.status)) s.complete += 1; else s.to_verify += 1;
      } else s.to_complete += 1;
    }
    return stats;
  }, [defs, records, isVisible]);

  if (!defs) return null;

  const renderInput = (item, rec) => {
    const value = rec?.value ?? null;
    const disabled = !canWrite || busy === item.code || rec?.applicability_status === "NOT_APPLICABLE";
    if (item.value_type === "YESNOUNK" || item.value_type === "YESNO") {
      const opts = item.value_type === "YESNOUNK" ? ["YES", "NO", "UNKNOWN"] : ["YES", "NO"];
      return (
        <div className="flex gap-1" role="group" aria-label={item.label}>
          {opts.map((o) => (
            <button key={o} type="button" disabled={disabled}
              onClick={() => save(item.code, o)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md border transition-colors ${
                value === o ? "bg-green-700 text-white border-green-700"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"} disabled:opacity-50`}
              data-testid={`gov-input-${item.code}-${o}`}>
              {TRI[o]}
            </button>
          ))}
          {busy === item.code && <Loader2 className="w-4 h-4 animate-spin text-slate-400 self-center" aria-hidden="true" />}
        </div>
      );
    }
    if (item.value_type === "ENUM") {
      return (
        <div className="flex flex-wrap gap-1" role="group" aria-label={item.label}>
          {item.options.map((o) => (
            <button key={o} type="button" disabled={disabled}
              onClick={() => save(item.code, o)}
              className={`px-2.5 py-1.5 text-xs font-semibold rounded-md border transition-colors ${
                value === o ? "bg-green-700 text-white border-green-700"
                  : "bg-white text-slate-600 border-slate-300 hover:bg-slate-100"} disabled:opacity-50`}
              data-testid={`gov-input-${item.code}-${o}`}>
              {o.replace(/_/g, " ").toLowerCase()}
            </button>
          ))}
        </div>
      );
    }
    const inputType = item.value_type === "DATE" ? "date" : item.value_type === "NUMBER" ? "number" : "text";
    return (
      <input type={inputType} disabled={disabled}
        value={textDrafts[item.code] ?? (value ?? "")}
        onChange={(e) => setTextDrafts((p) => ({ ...p, [item.code]: e.target.value }))}
        onBlur={(e) => {
          const v = e.target.value;
          if (String(value ?? "") !== v) save(item.code, v === "" ? null : v);
        }}
        className="h-9 w-full max-w-xs rounded-md border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-600 disabled:opacity-50"
        data-testid={`gov-input-${item.code}`} aria-label={item.label} />
    );
  };

  const sectionsToRender = defs.sections.filter(
    (s) => !["BODY", "C8", "C9", "SANCTIONS"].includes(s.code));

  return (
    <div className="space-y-3" data-testid="gov-sections">
      {defs.carry_forward?.length > 0 && (
        <div className="rounded-lg border border-sky-300 bg-sky-50 p-4" data-testid="carry-forward-banner">
          <p className="text-sm font-semibold text-sky-900 flex items-center gap-1.5">
            <History className="w-4 h-4" aria-hidden="true" />
            Abbiamo già {defs.carry_forward.length} informazioni dall'esercizio {defs.carry_forward[0].previous_year}
          </p>
          <ul className="text-xs text-sky-800 mt-2 space-y-1 max-h-32 overflow-y-auto">
            {defs.carry_forward.map((c) => (
              <li key={c.code}>• {c.label} → <strong>{TRI[c.previous_value] || String(c.previous_value)}</strong></li>
            ))}
          </ul>
          {canWrite && (
            <Button size="sm" className="mt-3 h-8 text-xs bg-sky-700 hover:bg-sky-800 text-white"
              onClick={() => confirmCarryForward(defs.carry_forward.map((c) => c.code))}
              data-testid="carry-forward-confirm-all">
              Conferma per il {period?.year} (restano bozze da verificare)
            </Button>
          )}
        </div>
      )}

      {sectionsToRender.map((section) => {
        const stats = sectionStats[section.code] || {};
        const items = defs.items.filter((i) => i.section === section.code);
        const isOpen = open[section.code] ?? stats.to_complete > 0;
        const legalCode = LEGAL_BY_SECTION[section.code];
        const legal = legalCode ? defs.legal_applicability?.[legalCode] : null;
        return (
          <div key={section.code} className="rounded-lg border border-slate-200 bg-white"
            data-testid={`gov-section-${section.code}`}>
            <button type="button" className="w-full flex items-center gap-3 p-3 text-left"
              onClick={() => setOpen((p) => ({ ...p, [section.code]: !isOpen }))}
              aria-expanded={isOpen} data-testid={`gov-section-toggle-${section.code}`}>
              {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" aria-hidden="true" />
                : <ChevronRight className="w-4 h-4 text-slate-400" aria-hidden="true" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900">{section.label}</p>
                <p className="text-xs text-slate-500">{section.description}</p>
              </div>
              {legal && (
                <Badge className={LEGAL_LABELS[legal.result]?.[1]} data-testid={`gov-legal-${section.code}`}
                  title={legal.source_reference}>
                  {LEGAL_LABELS[legal.result]?.[0]}
                </Badge>
              )}
              <div className="flex gap-1.5 text-[10px] font-semibold">
                {stats.complete > 0 && <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-800">{stats.complete} completi</span>}
                {stats.to_verify > 0 && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">{stats.to_verify} da verificare</span>}
                {stats.to_complete > 0 && <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{stats.to_complete} da completare</span>}
                {stats.not_applicable > 0 && <span className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-500">{stats.not_applicable} n.a.</span>}
              </div>
            </button>
            {isOpen && (
              <div className="border-t border-slate-100 divide-y divide-slate-50">
                {items.filter(isVisible).map((item) => {
                  const rec = records[item.code];
                  return (
                    <div key={item.code} className="p-3 space-y-2" data-testid={`gov-item-${item.code}`}>
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900">{item.label}</p>
                          <details className="text-xs text-slate-500 mt-0.5">
                            <summary className="cursor-pointer inline-flex items-center gap-1 hover:text-slate-700"
                              data-testid={`gov-help-${item.code}`}>
                              <HelpCircle className="w-3 h-3" aria-hidden="true" />
                              Perché me lo chiedi?
                            </summary>
                            <p className="mt-1"><strong>Perché:</strong> {item.why}</p>
                            <p className="mt-0.5"><strong>Cosa significa:</strong> {item.what}</p>
                          </details>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {rec?.status && rec.value !== null && rec.value !== undefined && (
                            <Badge className={STATUS_CLS[rec.status]} data-testid={`gov-status-${item.code}`}>
                              {STATUS_LABELS[rec.status]}
                            </Badge>
                          )}
                          {rec?.source_type === "CARRY_FORWARD" && (
                            <Badge variant="outline" className="text-[10px]">dall'anno precedente</Badge>
                          )}
                          {canVerify && rec && rec.value !== null && rec.value !== undefined
                            && !["VERIFIED", "APPROVED"].includes(rec.status) && (
                            <Button size="sm" variant="outline" className="h-6 text-[10px] px-2"
                              disabled={busy === item.code} onClick={() => verify(item.code)}
                              data-testid={`gov-verify-${item.code}`}>
                              <CheckCircle2 className="w-3 h-3 mr-1" aria-hidden="true" />Verifica
                            </Button>
                          )}
                          {canOverride && (
                            <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2 text-slate-500"
                              onClick={() => overrideNA(item.code,
                                rec?.applicability_status === "NOT_APPLICABLE" ? "APPLICABLE" : "NOT_APPLICABLE")}
                              data-testid={`gov-na-${item.code}`}>
                              {rec?.applicability_status === "NOT_APPLICABLE" ? "Rendi applicabile" : "Non applicabile"}
                            </Button>
                          )}
                        </div>
                      </div>
                      {renderInput(item, rec)}
                      {canMaturity && item.maturity_allowed && rec && rec.value !== null && rec.value !== undefined && (
                        <div className="flex items-center gap-2 pt-1 border-t border-dashed border-slate-100">
                          <Lock className="w-3 h-3 text-amber-500" aria-hidden="true" />
                          <label className="text-[10px] font-semibold text-slate-500"
                            htmlFor={`maturity-${item.code}`}>
                            Maturity (solo consulente):
                          </label>
                          <select id={`maturity-${item.code}`}
                            value={rec.consultant_maturity ?? ""}
                            onChange={(e) => e.target.value !== "" && setMaturity(item.code, e.target.value)}
                            className="h-7 rounded border border-slate-300 text-xs bg-white"
                            data-testid={`gov-maturity-${item.code}`}>
                            <option value="">—</option>
                            {Object.entries(defs.maturity_levels || {}).map(([lvl, lab]) => (
                              <option key={lvl} value={lvl}>{lvl} — {lab}</option>
                            ))}
                          </select>
                          {item.framework_scope?.length > 0 && (
                            <span className="text-[10px] text-slate-400 flex items-center gap-1">
                              <ShieldCheck className="w-3 h-3" aria-hidden="true" />
                              {item.framework_scope.join(" · ")}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
